import { message } from 'antd';
import helper from '../utils/helper';
import { getUserInfoApi } from '../utils/apisConsts';
import { errorMessage } from '../utils/commonStringsConsts';
import { useDispatch, useSelector } from 'react-redux';
import { getDataInfo, stepperMoving } from '../stores/actions';
import { KeyboardEvent, useState } from 'react';

type Account = {
    userID?: string;
    phone?: string;
    phoneNumber?: string;
    staffID?: string;
    userName?: string;
    companyName?: string;
    userStatus?: string;
};

const display = (value?: string | number | null) =>
    value === null || value === undefined || value === '' ? 'NA' : String(value);

const statusTone = (status?: string) => {
    const text = (status || '').toLowerCase();
    if (text.includes('active')) return 'is-ok';
    if (text.includes('suspend') || text.includes('block') || text.includes('reject')) return 'is-bad';
    if (text.includes('pending') || text.includes('review')) return 'is-warn';
    return '';
};

const accountPhone = (account: Account) => account.phone || account.phoneNumber || '';

const initials = (name?: string) => {
    const parts = (name || '').trim().split(/\s+/).filter(Boolean);
    if (!parts.length) return 'NA';
    return parts.slice(0, 2).map((part) => part[0]?.toUpperCase()).join('');
};

export default function UserAccounts() {
    const dispatch = useDispatch();
    const userAccounts = useSelector((state: { actionsReducer: { userAccounts: Account[] } }) => state.actionsReducer.userAccounts);
    const current = useSelector((state: { actionsReducer: { stepNumber: number } }) => state.actionsReducer.stepNumber);
    const [openingKey, setOpeningKey] = useState<string | null>(null);
    const accounts = Array.isArray(userAccounts) ? userAccounts : [];

    const openAccount = async (account: Account, key: string) => {
        if (openingKey) return;
        const userID = account.userID ? String(account.userID) : '';
        const phone = accountPhone(account);
        if (!userID && !phone) return;

        setOpeningKey(key);
        const params = userID
            ? { userID, phone: '' }
            : { userID: '', phone };
        try {
            const apiData: any = await helper.callApiRequest(getUserInfoApi, 'GET', params);
            const res = apiData.data;
            if (res?.status === 200) {
                dispatch(getDataInfo(res?.userInfo));
                dispatch(stepperMoving(current + 1));
                return;
            }
            message.error(res?.message, 5000);
        } catch (error) {
            message.error(errorMessage, 3000);
        }
        setOpeningKey(null);
    };

    const onRowKey = (event: KeyboardEvent<HTMLTableRowElement>, account: Account, key: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            openAccount(account, key);
        }
    };

    return (
        <div className={`user-accounts${openingKey ? ' is-busy' : ''}`}>
            <div className="user-accounts__panel">
                <div className="user-accounts__head">
                    <div>
                        <h3 className="user-accounts__title">Matching accounts</h3>
                        <p className="user-accounts__hint">Choose a row to open that profile.</p>
                    </div>
                    <span className="user-accounts__count">{accounts.length}</span>
                </div>

                {accounts.length === 0 ? (
                    <div className="user-accounts__empty">No accounts found for this search.</div>
                ) : (
                    <div className="user-accounts__scroll">
                        <table>
                            <thead>
                                <tr>
                                    <th>Account</th>
                                    <th>User ID</th>
                                    <th>Phone</th>
                                    <th>Staff ID</th>
                                    <th>Company</th>
                                    <th>Status</th>
                                </tr>
                            </thead>
                            <tbody>
                                {accounts.map((account, index) => {
                                    const phone = accountPhone(account);
                                    const key = `${account.userID || phone || 'account'}-${index}`;
                                    const opening = openingKey === key;
                                    return (
                                        <tr
                                            key={key}
                                            className={opening ? 'is-opening' : undefined}
                                            tabIndex={openingKey ? -1 : 0}
                                            aria-busy={opening}
                                            onClick={() => openAccount(account, key)}
                                            onKeyDown={(event) => onRowKey(event, account, key)}
                                        >
                                            <td>
                                                <span className="user-accounts__person">
                                                    <span className="user-accounts__avatar" aria-hidden="true">{initials(account.userName)}</span>
                                                    <span className="user-accounts__name">{display(account.userName)}</span>
                                                </span>
                                            </td>
                                            <td>{display(account.userID)}</td>
                                            <td>{display(phone)}</td>
                                            <td>{display(account.staffID)}</td>
                                            <td>{display(account.companyName)}</td>
                                            <td>
                                                <span className={`user-accounts__status ${statusTone(account.userStatus)}`}>
                                                    {display(account.userStatus)}
                                                </span>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        </div>
    );
}
